import React, { useEffect, useState } from "react";
import { IconTrash } from "@tabler/icons-react";
import RedFlag from "../components/RedFlag";
import { getCases, createCase, updateCase, deleteCase, generateCases, updateCcRubric } from "../services/api";

const EMPTY = { chief_complaint: "", age: "", sex: "", vignette: "", medical_history: "", must_capture: "", ground_truth_differential: "", reviewed: false, notes: "" };

const norm = (s) => (s || "").trim().toLowerCase();
const lines = (t) => (t || "").split("\n").map((s) => s.trim()).filter(Boolean);

// Card-face vignette snippet: first 140 chars.
const snippet = (v) => {
  const t = (v || "").trim();
  if (!t) return "(no vignette)";
  return t.length > 140 ? t.slice(0, 140) + "…" : t;
};

// Turn an existing case into the edit-form shape (arrays -> newline text; carry reviewed/notes so a
// PUT doesn't reset them, since CaseIn full-replaces).
const toForm = (c) => ({
  id: c.id,
  chief_complaint: c.chief_complaint || "",
  age: c.age || "",
  sex: c.sex || "",
  vignette: c.vignette || "",
  medical_history: c.medical_history || "",
  must_capture: (c.must_capture || []).join("\n"),
  ground_truth_differential: (c.ground_truth_differential || []).join("\n"),
  reviewed: !!c.reviewed,
  notes: c.notes || "",
});

export default function CaseLibrary() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);        // new/edit case form
  const [redFlag, setRedFlag] = useState(null);  // {cc, text} — the group-header Red Flags editor
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = () => getCases().then(setData);
  useEffect(() => { load(); }, []);

  // Resolved Red Flags for a chief complaint (from the grouped data) — for the case-editor reference.
  const redFlagsFor = (cc) => {
    const g = (data?.groups || []).find((x) => norm(x.chief_complaint) === norm(cc));
    return g ? (g.red_flags || []) : [];
  };

  const submit = async () => {
    setBusy(true); setMsg("");
    const body = {
      chief_complaint: form.chief_complaint,
      age: form.age,
      sex: form.sex,
      vignette: form.vignette,
      medical_history: form.medical_history,
      must_capture: lines(form.must_capture),
      ground_truth_differential: lines(form.ground_truth_differential),
      reviewed: !!form.reviewed,
      notes: form.notes || "",
    };
    try {
      if (form.id) await updateCase(form.id, body);   // edit -> re-stamps case_content_hash server-side
      else await createCase(body);                    // create -> auto-creates the (empty) Red Flags bucket
      setForm(null); await load();
    } catch (e) { setMsg(form.id ? "Save failed." : "Create failed."); }
    setBusy(false);
  };

  const gen = async () => {
    const cc = window.prompt("Chief complaint to generate cases for:", "chest pain");
    if (!cc) return;
    setBusy(true); setMsg("Generating drafts…");
    try {
      const { drafts } = await generateCases({ chief_complaint: cc, n: 2 });
      for (const d of drafts) {
        await createCase({ chief_complaint: cc, age: d.age || "", sex: d.sex || "", vignette: d.vignette,
          medical_history: d.medical_history || "",
          must_capture: d.must_capture || [], ground_truth_differential: d.ground_truth_differential || [] });
      }
      setMsg(`Added ${drafts.length} draft case(s).`); await load();
    } catch (e) { setMsg("Generation failed (needs an OpenAI key)."); }
    setBusy(false);
  };

  const saveRedFlags = async () => {
    setBusy(true); setMsg("");
    try { await updateCcRubric(redFlag.cc, lines(redFlag.text)); setRedFlag(null); await load(); }
    catch (e) { setMsg("Red Flags save failed."); }
    setBusy(false);
  };

  const remove = async (id) => { if (window.confirm("Delete this case?")) { await deleteCase(id); await load(); } };

  if (!data) return <div className="page-head"><h2>Case Library</h2></div>;

  return (
    <>
      <div className="page-head"><h2>Case Library</h2></div>
      <div className="flex" style={{ marginBottom: 12 }}>
        <button className="btn sm" onClick={() => setForm({ ...EMPTY })}>+ New case</button>
        <button className="btn sm" disabled={busy} onClick={gen}>Generate drafts (LLM)</button>
        <div className="spacer" />
        <span className="muted">{data.total} case(s)</span>
      </div>
      {msg && <div className="muted" style={{ marginBottom: 10 }}>{msg}</div>}

      {form && (
        <div className="card">
          <h3>{form.id ? `Edit case ${form.id.slice(0, 8)}` : "New case"}</h3>
          <div className="row">
            <div className="col">
              <label className="f">Chief complaint</label>
              <input type="text" list="cc-options" value={form.chief_complaint}
                     onChange={(e) => setForm({ ...form, chief_complaint: e.target.value })} />
              <datalist id="cc-options">
                {(data.groups || []).filter((g) => g.chief_complaint !== "(unspecified)")
                  .map((g) => <option key={g.chief_complaint} value={g.chief_complaint} />)}
              </datalist>
              <div className="flex" style={{ marginTop: 10, alignItems: "flex-end", gap: 16 }}>
                <div style={{ maxWidth: 120 }}>
                  <label className="f">Age</label>
                  <input type="text" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} />
                </div>
                <div>
                  <label className="f">Sex</label>
                  <div className="flex" style={{ gap: 14 }}>
                    {["Male", "Female"].map((s) => (
                      <label key={s} className="checkline" style={{ margin: 0 }}>
                        <input type="radio" name="sex" checked={form.sex === s} onChange={() => setForm({ ...form, sex: s })} /> {s}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className="muted" style={{ fontSize: 11, marginTop: 8 }}>
                Only age + sex are given to the system under test (new-patient model). The vignette and
                medical history are known only to the patient and must be interviewed out.
              </div>
              <label className="f" style={{ marginTop: 10 }}>Vignette — presentation (patient emulator)</label>
              <textarea rows={4} value={form.vignette} onChange={(e) => setForm({ ...form, vignette: e.target.value })} />
              <label className="f" style={{ marginTop: 10 }}>Medical history — patient reports when asked (patient emulator)</label>
              <textarea rows={3} value={form.medical_history} onChange={(e) => setForm({ ...form, medical_history: e.target.value })} />
            </div>
            <div className="col">
              <label className="f">Must Capture — case-specific (one per line)</label>
              <textarea rows={5} value={form.must_capture} onChange={(e) => setForm({ ...form, must_capture: e.target.value })} />

              <label className="f" style={{ marginTop: 10 }}><RedFlag /> Red Flags for “{form.chief_complaint || "…"}” — shared</label>
              <div className="muted" style={{ fontSize: 12, border: "1px solid var(--line)", borderRadius: 6, padding: "7px 9px" }}>
                {redFlagsFor(form.chief_complaint).length
                  ? redFlagsFor(form.chief_complaint).join(" · ")
                  : "None yet for this complaint."}
                <div style={{ marginTop: 4 }}>Shared across all “{form.chief_complaint || "…"}” cases — edit on the group header below.</div>
              </div>

              <label className="f" style={{ marginTop: 10 }}>Ground-truth differential (one per line)</label>
              <textarea rows={3} value={form.ground_truth_differential} onChange={(e) => setForm({ ...form, ground_truth_differential: e.target.value })} />
            </div>
          </div>
          <label className="checkline" style={{ marginTop: 10 }}>
            <input type="checkbox" checked={!!form.reviewed} onChange={(e) => setForm({ ...form, reviewed: e.target.checked })} /> Reviewed
          </label>
          <div className="flex" style={{ marginTop: 12 }}>
            <button className="btn primary sm" disabled={busy || !form.chief_complaint} onClick={submit}>
              {form.id ? "Save changes" : "Save case"}
            </button>
            <button className="btn sm" onClick={() => setForm(null)}>Cancel</button>
          </div>
        </div>
      )}

      {data.groups.map((g) => {
        const editing = redFlag && redFlag.cc === g.chief_complaint;
        const isReal = g.chief_complaint !== "(unspecified)";
        return (
          <div key={g.chief_complaint}>
            <div className="grp" style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span>{g.chief_complaint} <span className="n">{g.cases.length}</span></span>
              <span className="muted" style={{ fontSize: 12, display: "inline-flex", alignItems: "center", gap: 4 }}>
                <RedFlag /> {(g.red_flags || []).length} red flag(s)</span>
              {isReal && (
                <button className="btn sm" onClick={() => setRedFlag(editing ? null
                  : { cc: g.chief_complaint, text: (g.red_flags || []).join("\n") })}>
                  {editing ? "Close" : (g.red_flags || []).length ? "Edit Red Flags"
                    : <><RedFlag /> Add Red Flags</>}
                </button>
              )}
            </div>
            {editing && (
              <div className="card" style={{ padding: "12px 14px" }}>
                <label className="f"><RedFlag /> Red Flags for “{g.chief_complaint}” — shared across all {g.cases.length} case(s), one per line</label>
                {(g.red_flags || []).length === 0 && (
                  <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>No red flags yet for {g.chief_complaint} — add them.</div>
                )}
                <textarea rows={4} value={redFlag.text} onChange={(e) => setRedFlag({ ...redFlag, text: e.target.value })} />
                <div className="flex" style={{ marginTop: 8 }}>
                  <button className="btn primary sm" disabled={busy} onClick={saveRedFlags}>Save Red Flags</button>
                  <button className="btn sm" onClick={() => setRedFlag(null)}>Cancel</button>
                  <span className="muted" style={{ fontSize: 12 }}>Applies to every “{g.chief_complaint}” case.</span>
                </div>
              </div>
            )}
            <div className="case-colhead">
              <div className="lbl dx">Target DX</div>
              <div className="lbl">Patient</div>
              <div className="lbl">Vignette</div>
              <div className="lbl cap">Capture</div>
              <div className="a-spacer" />
            </div>
            {g.cases.map((c) => (
              <div className="card" key={c.id} style={{ padding: "12px 14px" }}>
                <div className="case-grid">
                  <div className="dx">
                    <span className={`status-dot ${c.reviewed ? "ok" : "off"}`}
                          title={c.reviewed ? "Reviewed" : "Not reviewed"} />
                    {(c.ground_truth_differential || [])[0] || "—"}
                  </div>
                  <div className="demo">{[c.age, c.sex].filter(Boolean).join(" · ") || "—"}</div>
                  <div className="vig">{snippet(c.vignette)}</div>
                  <div className="cap"><span className="chip n">{(c.must_capture || []).length}</span></div>
                  <div className="actions">
                    <button className="btn sm" onClick={() => { setForm(toForm(c)); setMsg(""); window.scrollTo({ top: 0, behavior: "smooth" }); }}>Edit</button>
                    <button className="btn sm icon danger" title="Delete case" aria-label="Delete case"
                            onClick={() => remove(c.id)}><IconTrash size={14} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        );
      })}
    </>
  );
}
